import ErrorMessage from "components/common/ErrorMessage";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { fetchVendorReliability, downloadVendorReliabilityCSV } from "slices/dashboardSlice";
import { DashboardSelector } from "slices/dashboardSlice";
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import FullPageLoader from "components/common/FullPageLoader";
import { fetchVendors } from "slices/vendorSlice";
import VendorReliabilityTable from "components/dashboard/VendorReliabilityTable";
import toast from "react-hot-toast";

const DatesInputs = (({ control, errors }) => {
  const startDateRef = React.useRef(null);
  const endDateRef = React.useRef(null);

  return (
    <>
      <div className="row-primary">
        <h2>Start Date*</h2>
        <Controller
          name="start_date"
          control={control}
          render={({ field }) =>
            <div className="input-image basic_bases">
              <input ref={startDateRef} id='start_date' type="date" name='start_date' placeholder='Start Date'
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const dateString = moment(e.target.value).format('YYYY/MM/DD')
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />
            </div>
          } />
        {errors.start_date && <ErrorMessage text={errors.start_date.message} />}
      </div>
      <div className="row-primary">
        <h2>End Date*</h2>
        <Controller
          name="end_date"
          control={control}
          render={({ field }) =>
            <div className="input-image basic_bases">
              <input ref={endDateRef} type="date" name='end_date' placeholder='End Date'
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const dateString = moment(e.target.value).format('YYYY/MM/DD')
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />
            </div>
          } />
        {errors.end_date && <ErrorMessage text={errors.end_date.message} />}
      </div>
    </>
  )
});

function VendorReliability() {
  const dispatch = useDispatch()
  const { loader } = useSelector(DashboardSelector);

  const defaultValues = {
    start_date: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    vendor_id: '',
  };

  const { clearErrors, handleSubmit, watch, formState: { errors }, control, setValue, setError: setErrors } = useForm({
    defaultValues: defaultValues
  });

  const [filteredVendors, setFilteredVendors] = useState(null);
  const [filterKey, setFilterKey] = useState(0);

  const watchedValues = watch(['start_date', 'end_date', 'vendor_id']);
  const [start_date, end_date, vendor_id] = watchedValues;

  const paginationParams = React.useMemo(() => ({
    start_date: start_date ? moment(start_date).format('YYYY-MM-DD') : undefined,
    end_date: end_date ? moment(end_date).format('YYYY-MM-DD') : undefined,
    vendor_id,
  }), [start_date, end_date, vendor_id]);

  // Returns true (and sets the error) when the range is invalid
  const rangeInvalid = (startDate, endDate) => {
    if (moment(endDate).isBefore(moment(startDate))) {
      setErrors('end_date', { message: 'End Date must be greater than Start Date' });
      return true;
    }
    if (moment(endDate).isAfter(moment(startDate).add(3, 'months'))) {
      setErrors('end_date', { message: 'Date range cannot exceed 3 months' });
      return true;
    }
    return false;
  };

  useEffect(() => {
    dispatch(fetchVendorReliability(1, {
      start_date: defaultValues.start_date,
      end_date: defaultValues.end_date,
    }));
  }, [])

  const onSubmit = (data) => {
    const startDate = moment(data.start_date).format('YYYY-MM-DD');
    const endDate = moment(data.end_date).format('YYYY-MM-DD');
    if (rangeInvalid(startDate, endDate)) return;

    dispatch(fetchVendorReliability(1, {
      start_date: startDate,
      end_date: endDate,
      vendor_id: data.vendor_id,
    }));
    setFilterKey(prev => prev + 1);
    clearErrors();
  }

  // #region Vendor dropdown
  const filteredVendorsLoadOptions = React.useCallback(async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchVendors(page, { pageLength: 20, sortBy: "name", sortDirection: "asc", search: inputValue }, true, true));
    return {
      options: result?.data?.map((val) => ({ value: val.id, label: val.name || "" })) || [],
      hasMore: page < Number(result?.last_page),
      additional: { page: page + 1 },
    };
  }, [dispatch]);
  // #endregion

  const handleResetFields = () => {
    setValue("start_date", defaultValues.start_date);
    setValue("end_date", defaultValues.end_date);
    setValue("vendor_id", "");
    setFilteredVendors(null);
    dispatch(fetchVendorReliability(1, {
      start_date: defaultValues.start_date,
      end_date: defaultValues.end_date,
    }));
    setFilterKey(prev => prev + 1);
    clearErrors()
  };

  const handleDownload = () => {
    const startDate = moment(start_date).format('YYYY-MM-DD');
    const endDate = moment(end_date).format('YYYY-MM-DD');
    if (rangeInvalid(startDate, endDate)) {
      toast.error('Date range cannot exceed 3 months', { className: "errorToast" });
      return;
    }
    dispatch(downloadVendorReliabilityCSV({ start_date: startDate, end_date: endDate, vendor_id }));
  };

  return (
    <>
      <div className="content">
        {loader && <FullPageLoader />}
        <div className="row_main_head">
          <h2 className="main_heading">Vendor Reliability</h2>
        </div>
        <div className="card_table">
          <form className="search_form_ad" onSubmit={handleSubmit(onSubmit)}>
            <div className="row-primary dahboard_filter">
              <DatesInputs control={control} errors={errors} />

              <div className="row-primary">
                <h2>Vendor</h2>
                <Controller
                  name="vendor_id"
                  control={control}
                  render={({ field }) =>
                    <div className="basic_bases">
                      <AsyncPaginate
                        menuPlacement='auto'
                        styles={{ ...selectCustomStyles }}
                        debounceTimeout={300}
                        value={filteredVendors}
                        loadOptions={filteredVendorsLoadOptions}
                        isClearable
                        onChange={(obj) => {
                          setFilteredVendors(obj);
                          field.onChange(obj?.value);
                        }}
                        additional={{ page: 1 }}
                      />
                    </div>
                  }
                />
              </div>
            </div>

            <div className="row-primary btns_area row_10">
              <SubmitButton handleSubmit={handleSubmit(onSubmit)} buttonType="button" btnText="Filter" className={'green'} />
              <SubmitButton buttonType="button" className={'lightBlue'} btnText="Reset" handleSubmit={handleResetFields} />
              <button
                type="button"
                style={{ marginLeft: 'auto' }}
                className="main_button"
                onClick={handleDownload}
              >
                Download Report
              </button>
            </div>

            <div className="col-12">
              <VendorReliabilityTable paginationParams={paginationParams} filterKey={filterKey} />
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default VendorReliability;
